import { createApiClient } from "./api/client.js";
import type { FormLinkPayload, FormEmbedConfig } from "./types.js";
import type { components } from "./api/v1.js";
import type { FormLinkResponse } from "./schema-types.js";

export class FormVueApiError extends Error {
  constructor(public status: number, message: string, public errorCode?: string) {
    super(message);
    this.name = "FormVueApiError";
  }
}

const ERROR_MESSAGES: Record<string, string> = {
  formNotProvided: "This form is not available. Please contact support.",
  formIntegrationNotFound: "This form is not available. Please contact support.",
  formLinkCreationFailed: "Something went wrong. Please try again.",
  phoneNumberNotProvided: "Please enter a valid phone number.",
  invalidPhoneNumber: "Please enter a valid phone number.",
  formLinkNotFound: "This request is no longer valid. Please start over.",
  smsDeliveryFailed: "We couldn't deliver the SMS. Please check your number and try again.",
  smsRateLimited: "Too many requests. Please wait a few minutes and try again.",
};

function extractErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const errors = (error as Record<string, unknown>).errors;
  if (!errors || typeof errors !== "object") return undefined;
  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}

function extractErrorMessage(status: number, error: unknown): { message: string; code?: string } {
  const code = extractErrorCode(error);
  if (code && ERROR_MESSAGES[code]) {
    return { message: ERROR_MESSAGES[code], code };
  }

  if (status === 429) return { message: ERROR_MESSAGES.smsRateLimited };

  if (error && typeof error === "object") {
    const obj = error as Record<string, unknown>;
    const validation = (obj.errors as Record<string, unknown>)?.validation;
    if (Array.isArray(validation) && validation.length > 0) {
      return { message: String(validation[0]) };
    }
    if (typeof obj.message === "string" && obj.message) {
      return { message: obj.message };
    }
    if (typeof obj.error === "string" && obj.error) {
      return { message: obj.error };
    }
  }
  if (typeof error === "string" && error) return { message: error };

  return { message: "Something went wrong. Please try again." };
}

export async function fetchFormEmbed(
  embedApiUrl: string,
  shortId: string
): Promise<FormEmbedConfig> {
  const client = createApiClient(embedApiUrl);
  const { data, error, response } = await client.GET("/api/v1/form-embeds/p/{shortId}", {
    params: { path: { shortId } },
  });

  if (data) return data as unknown as FormEmbedConfig;
  const extracted = extractErrorMessage(response.status, error);
  throw new FormVueApiError(response.status, extracted.message, extracted.code);
}

type CreateFormLinkBody = components["schemas"]["CreateFormLinkDto"];

/**
 * Shapes the SDK payload as the platform's generated `CreateFormLinkDto`, so a
 * payload field the platform does not accept fails to compile here. UTM keys
 * the page did not carry are dropped rather than sent as undefined.
 */
export function toCreateFormLinkBody(payload: FormLinkPayload): CreateFormLinkBody {
  const utm: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload.utm)) {
    if (typeof value === "string") utm[key] = value;
  }
  return {
    formId: payload.formId,
    entityId: payload.entityId,
    phoneNumber: payload.phoneNumber,
    utmSource: payload.utmSource,
    preData: payload.preData,
    embedId: payload.embedId,
    campaignId: payload.campaignId,
    campaignSlug: payload.campaignSlug,
    utm,
    metadata: payload.metadata,
    sendSMS: payload.sendSMS,
  };
}

export async function submitPhoneNumber(
  embedApiUrl: string,
  payload: FormLinkPayload
): Promise<FormLinkResponse> {
  const client = createApiClient(embedApiUrl);
  const { data, error, response } = await client.POST("/api/v1/form-links", {
    body: toCreateFormLinkBody(payload),
  });

  if (data) return data as unknown as FormLinkResponse;
  const extracted = extractErrorMessage(response.status, error);
  throw new FormVueApiError(response.status, extracted.message, extracted.code);
}

/**
 * Resends the SMS for an existing form-link. Used by the content/coupon flow,
 * where the initial form-link is created with `sendSMS: false` and the SMS is
 * sent only once the user confirms via the coupon CTA.
 */
export async function resendFormLink(
  embedApiUrl: string,
  id: string
): Promise<FormLinkResponse> {
  const client = createApiClient(embedApiUrl);
  const { data, error, response } = await client.POST("/api/v1/form-links/{id}/resend", {
    params: { path: { id } },
  });

  if (data) return data as unknown as FormLinkResponse;
  const extracted = extractErrorMessage(response.status, error);
  throw new FormVueApiError(response.status, extracted.message, extracted.code);
}
